import { useId } from 'react';
import type { TrendPoint } from '../model/chart-data.js';

/**
 * Kirim va chiqim dinamikasi — ikkita qatorli maydon grafigi.
 *
 * Kutubxonasiz, sof SVG: grafik uchun butun chizma kutubxonasini olib kelish
 * bu ekranga qimmat tushardi va u mavzu (yorug'/qorong'i) bilan baribir
 * qo'lda bog'lanardi. Ranglar CSS o'zgaruvchilaridan olinadi, ya'ni mavzu
 * almashganda grafik o'zi moslashadi.
 *
 * O'lcham `viewBox` da — konteyner qanchalik keng bo'lsa, shunchalik
 * cho'ziladi va matn ham u bilan birga o'sadi.
 */

const W = 720;
const H = 260;
const PAD = { top: 16, right: 12, bottom: 26, left: 40 };

const INNER_W = W - PAD.left - PAD.right;
const INNER_H = H - PAD.top - PAD.bottom;

export interface TrendChartProps {
  points: readonly TrendPoint[];
  receiptsLabel: string;
  issuesLabel: string;
  caption: string;
}

/** Qatorni silliq egri chiziqqa aylantiradi (Catmull-Rom → kubik Bezier). */
const smoothPath = (coords: readonly { x: number; y: number }[]): string => {
  if (coords.length === 0) return '';
  if (coords.length === 1) return `M ${coords[0]!.x} ${coords[0]!.y}`;

  let path = `M ${coords[0]!.x} ${coords[0]!.y}`;
  for (let i = 0; i < coords.length - 1; i += 1) {
    const p0 = coords[Math.max(0, i - 1)]!;
    const p1 = coords[i]!;
    const p2 = coords[i + 1]!;
    const p3 = coords[Math.min(coords.length - 1, i + 2)]!;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    path += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return path;
};

/** O'q bo'yicha "chiroyli" yuqori chegara — 50 ga yaxlitlanadi. */
const niceMax = (value: number): number => Math.ceil(value / 50) * 50;

export const TrendChart = ({ points, receiptsLabel, issuesLabel, caption }: TrendChartProps) => {
  const id = useId();
  const max = niceMax(
    Math.max(1, ...points.map((point) => Math.max(point.receipts, point.issues))),
  );

  const xOf = (index: number): number =>
    PAD.left + (points.length < 2 ? INNER_W / 2 : (index / (points.length - 1)) * INNER_W);
  const yOf = (value: number): number => PAD.top + INNER_H - (value / max) * INNER_H;

  const receipts = points.map((point, index) => ({ x: xOf(index), y: yOf(point.receipts) }));
  const issues = points.map((point, index) => ({ x: xOf(index), y: yOf(point.issues) }));

  const areaOf = (coords: readonly { x: number; y: number }[]): string =>
    `${smoothPath(coords)} L ${xOf(points.length - 1)} ${PAD.top + INNER_H} L ${xOf(0)} ${PAD.top + INNER_H} Z`;

  // To'rt chiziq — o'qishga tayanch. Beshtadan ko'pi grafikni "qafas" qiladi.
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ({
    y: PAD.top + INNER_H - ratio * INNER_H,
    value: Math.round(max * ratio),
  }));

  return (
    <figure className="flex flex-col gap-3">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        role="img"
        aria-label={caption}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id={`${id}-receipts`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-accent)" stopOpacity="0.32" />
            <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0" />
          </linearGradient>
          <linearGradient id={`${id}-issues`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-info)" stopOpacity="0.26" />
            <stop offset="100%" stopColor="var(--color-info)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {ticks.map((tick) => (
          <g key={tick.value}>
            <line
              x1={PAD.left}
              y1={tick.y}
              x2={W - PAD.right}
              y2={tick.y}
              stroke="var(--color-border)"
              strokeWidth={1}
            />
            <text
              x={PAD.left - 8}
              y={tick.y + 4}
              textAnchor="end"
              fontSize="11"
              fill="var(--color-text-faint)"
            >
              {tick.value}
            </text>
          </g>
        ))}

        <path d={areaOf(receipts)} fill={`url(#${id}-receipts)`} />
        <path d={areaOf(issues)} fill={`url(#${id}-issues)`} />

        <path
          d={smoothPath(issues)}
          fill="none"
          stroke="var(--color-info)"
          strokeWidth={2}
          strokeLinecap="round"
        />
        <path
          d={smoothPath(receipts)}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth={2.5}
          strokeLinecap="round"
        />

        {/* Kalit INDEKS bo'yicha: yorliq ataylab takrorlanadi — davr uzun
            bo'lsa faqat har n-nuqtaga sana yoziladi, qolgani bo'sh satr. */}
        {points.map((point, index) => (
          <g key={index}>
            <circle cx={xOf(index)} cy={yOf(point.receipts)} r={2.5} fill="var(--color-accent)" />
            <text
              x={xOf(index)}
              y={H - 8}
              textAnchor="middle"
              fontSize="11"
              fill="var(--color-text-faint)"
            >
              {point.label}
            </text>
          </g>
        ))}
      </svg>

      <figcaption className="flex flex-wrap items-center gap-4 text-xs text-text-muted">
        <span className="flex items-center gap-1.5">
          <span aria-hidden className="size-2.5 rounded-full bg-accent" />
          {receiptsLabel}
        </span>
        <span className="flex items-center gap-1.5">
          <span aria-hidden className="size-2.5 rounded-full bg-info" />
          {issuesLabel}
        </span>
      </figcaption>
    </figure>
  );
};
